import { useMemo, useEffect, useRef, useCallback } from "react";
import useIncident from "./useIncident";
import useIncidentTypes from "./useIncidentTypes";
import { useStoreWrapper, useTableFiltersWrapper } from "../store/store";
import { shallow } from "zustand/shallow";
import { getFormattedDraftIncidentsData } from "../utils/helpers/incidentListUtils";

interface UseFormattedDraftIncidentsProps {
  debouncedSearchQuery: string;
}

export function useFormattedDraftIncidents({
  debouncedSearchQuery,
}: UseFormattedDraftIncidentsProps) {
  const { user, userFacilities } = useStoreWrapper((state) => ({
    user: state.user,
    userFacilities: state.userFacilities,
  }), shallow);

  const { incidentStartDateFilter, incidentEndDateFilter } =
    useTableFiltersWrapper((state) => ({
      incidentStartDateFilter: state.incidentStartDateFilter,
      incidentEndDateFilter: state.incidentEndDateFilter,
    }), shallow);

  const { getSavedIncidents, deleteDraftIncidentFromList } = useIncident({
    userId: user.userId,
    startDate: incidentStartDateFilter,
    endDate: incidentEndDateFilter,
  });

  const {
    isLoading: savedIncidentsLoading,
    data: draftIncidentsData,
    refetch: refetchSavedIncident,
    isFetching,
    error: draftsError,
  } = getSavedIncidents;

  const { incidentTypes } = useIncidentTypes();

  // Keep a stable ref for refetch to avoid it as a dependency
  const refetchRef = useRef(refetchSavedIncident);
  refetchRef.current = refetchSavedIncident;

  const stableRefetch = useCallback(() => {
    refetchRef.current();
  }, []);

  // Refetch when filters change — refetch itself is NOT a dependency
  useEffect(() => {
    stableRefetch();
  }, [
    stableRefetch,
    debouncedSearchQuery,
    incidentStartDateFilter,
    incidentEndDateFilter,
  ]);

  const draftIncidents = useMemo(
    () =>
      savedIncidentsLoading || draftsError
        ? []
        : draftIncidentsData?.data?.data?.IncidentDrafts || [],
    [savedIncidentsLoading, draftsError, draftIncidentsData]
  );

  const formattedDrafts = useMemo(
    () =>
      getFormattedDraftIncidentsData(
        draftIncidents,
        userFacilities,
        incidentTypes
      ),
    [draftIncidents, userFacilities, incidentTypes]
  );

  return {
    formattedDrafts,
    draftIncidents,
    isLoading: savedIncidentsLoading,
    isFetching,
    refetchSavedIncident,
    deleteDraftIncidentFromList,
    error: draftsError,
  };
}
